import { agentName } from 'shared/branding';

// Someone who hasn't joined has only a placeholder sign-in name, which means nothing to
// anyone: they go by what the people adding them know, the address they invited.
export const personName = (person) =>
  [person?.first_name, person?.last_name].filter(Boolean).join(' ')
  || (person?.preconfigured ? person.invitation?.email || person.job_title || 'New person' : person?.email)
  || 'Unnamed person';

export const FAILED_DELIVERY = ['failed', 'unknown', 'bounced', 'blocked', 'dropped'];

/** Team members, each not yet joined carrying the invitation that is still open for them. */
export const withInvitations = (team = {}) => (team.members || []).map((person) => (person.preconfigured ? {
  ...person,
  invitation: (team.invitations || []).find((invite) => ['pending', 'expired'].includes(invite.status) && String(invite.user_profile_id) === String(person.id)),
} : person));

/** Where someone who hasn't joined stands, or null for anyone who has. */
export function joinStatus(person) {
  if (!person?.preconfigured) return null;
  const invite = person.invitation;
  if (!invite) return { key: 'not_invited', label: 'Not invited yet' };
  if (FAILED_DELIVERY.includes(invite.delivery_status)) return { key: 'not_delivered', label: 'Invitation not delivered' };
  if (invite.status === 'expired') return { key: 'expired', label: 'Invitation expired' };
  return { key: 'invited', label: 'Invited' };
}

export const isActiveMember = (person) => person.is_active && person.membership_active !== false;

/**
 * Human reporting lines only. Ownership and role sharing are separate relationships.
 * Members whose access is inactive stay on the chart, outside every reporting line,
 * so their access can still be reviewed. Their Agents count as owner unavailable and
 * are also listed on the inactive owner, so each side shows the other. Retired Agents
 * are left off; the Agents page lists them for restoring.
 */
export function buildOrgChart(team = {}, allAgents = []) {
  const agents = allAgents.filter(agent => agent.status !== 'retired');
  const members = withInvitations(team);
  const people = members.filter(isActiveMember);
  const peopleById = new Map(people.map(person => [String(person.id), person]));
  const rolesById = new Map((team.roles || []).map(role => [String(role.id), role]));
  // Access follows role_id alone (the server ignores a bare role slug), so the chart does too.
  const roleOf = (person) => rolesById.get(String(person.role_id)) || null;
  const agentsByOwner = new Map();
  const sharedAgents = [];
  const unplacedAgents = [];
  for (const item of agents) {
    const agent = { ...item, access_roles: (item.access_role_ids || []).map(id => rolesById.get(String(id))).filter(Boolean) };
    if (agent.is_lead) sharedAgents.push(agent);
    else if (peopleById.has(String(agent.owner_user_profile_id))) {
      const ownerId = String(agent.owner_user_profile_id);
      if (!agentsByOwner.has(ownerId)) agentsByOwner.set(ownerId, []);
      agentsByOwner.get(ownerId).push(agent);
    } else unplacedAgents.push(agent);
  }
  const nodes = people.map(person => ({
    id: `person:${person.id}`, name: personName(person), role: roleOf(person),
    people: [{ ...person, manager: peopleById.get(String(person.reports_to_user_profile_id)) || null,
      agents: (agentsByOwner.get(String(person.id)) || []).sort((a, b) => Number(a.connection?.status !== 'connected') - Number(b.connection?.status !== 'connected') || agentName(a).localeCompare(agentName(b))) }],
  })).sort((a, b) => a.name.localeCompare(b.name));
  const edges = people.filter(person => peopleById.has(String(person.reports_to_user_profile_id)) && String(person.id) !== String(person.reports_to_user_profile_id)).map(person => ({
    id: `${person.reports_to_user_profile_id}->${person.id}`, source: `person:${person.reports_to_user_profile_id}`, target: `person:${person.id}`,
    reports: [{ person, manager: peopleById.get(String(person.reports_to_user_profile_id)) }],
  }));
  const connected = new Set(edges.flatMap(edge => [edge.source, edge.target]));
  nodes.forEach(node => { node.connected = connected.has(node.id); });
  const inactive = members.filter(person => !isActiveMember(person)).map(person => ({
    id: `person:${person.id}`, name: personName(person), role: roleOf(person),
    inactive: true, connected: false,
    people: [{ ...person, manager: null, agents: unplacedAgents.filter(agent => String(agent.owner_user_profile_id) === String(person.id)) }],
  })).sort((a, b) => a.name.localeCompare(b.name));
  return { nodes: [...nodes, ...inactive], edges, people, sharedAgents, unplacedAgents, agentCount: agents.length, roleCount: (team.roles || []).length };
}

/** Active members without an operational role: they have no business app or data access. */
export const withoutRole = (node) => !node.role && !node.inactive;

/**
 * Narrow the chart to people matching `query` and, with `roleless`, to those without
 * an operational role. Their managers stay visible so the reporting context remains.
 */
export function filterOrgChart(chart, query, { roleless = false } = {}) {
  const search = query.trim().toLocaleLowerCase();
  if (!search && !roleless) return { ...chart, matchingIds: null };
  const matchesAgent = (agent) => !roleless && `${agentName(agent)} ${agent.title || ''}`.toLocaleLowerCase().includes(search);
  const matchingIds = new Set(chart.nodes.filter((node) => (!roleless || withoutRole(node)) && [
    node.name, node.role?.name, node.role?.description,
    // An inactive owner's Agents are matched under "Owner unavailable", not on the owner.
    ...node.people.flatMap((person) => [personName(person), person.email, person.invitation?.email, person.job_title, person.preconfigured_username,
      ...(node.inactive ? [] : person.agents.flatMap((agent) => [agentName(agent), agent.title]))]),
  ].filter(Boolean).join(' ').toLocaleLowerCase().includes(search)).map((node) => node.id));
  const visible = new Set(matchingIds);
  // Retain every ancestor, including multiple managers; a visited set bounds cycles.
  const pending = [...visible];
  while (pending.length) {
    const child = pending.pop();
    for (const edge of chart.edges) {
      if (edge.target === child && !visible.has(edge.source)) {
        visible.add(edge.source);
        pending.push(edge.source);
      }
    }
  }
  return {
    ...chart, matchingIds,
    nodes: chart.nodes.filter((node) => visible.has(node.id)),
    edges: chart.edges.filter((edge) => visible.has(edge.source) && visible.has(edge.target)),
    sharedAgents: chart.sharedAgents.filter(matchesAgent),
    unplacedAgents: chart.unplacedAgents.filter(matchesAgent),
  };
}

/** Avatar initials: the first and last word of a name, or of an email's local part. */
export const initials = (name) => {
  const words = String(name || '').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return ((words[0]?.[0] || '') + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase() || '?';
};

/**
 * Reporting trees with managers above their reports, largest first. A reporting loop
 * has no top, so it is opened at one of its members (marked `loop`) to keep everyone visible.
 */
export function buildReportingForest(nodes, edges) {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const order = new Map(nodes.map((node, index) => [node.id, index]));
  const reportsOf = new Map();
  const managerOf = new Map();
  for (const edge of edges) {
    if (!byId.has(edge.source) || !byId.has(edge.target)) continue;
    if (!reportsOf.has(edge.source)) reportsOf.set(edge.source, []);
    reportsOf.get(edge.source).push(edge.target);
    managerOf.set(edge.target, edge.source);
  }
  const visited = new Set();
  const grow = (id, loop = false) => {
    visited.add(id);
    const reports = [];
    for (const child of [...(reportsOf.get(id) || [])].sort((a, b) => order.get(a) - order.get(b))) {
      if (!visited.has(child)) reports.push(grow(child));
    }
    return { node: byId.get(id), reports, loop, size: 1 + reports.reduce((total, child) => total + child.size, 0) };
  };
  const trees = nodes.filter((node) => !managerOf.has(node.id)).map((node) => grow(node.id));
  for (const node of nodes) {
    if (visited.has(node.id)) continue;
    // Everyone left is in a loop or below one; walk up until a manager repeats.
    const seen = new Set();
    let current = node.id;
    while (!seen.has(current)) { seen.add(current); current = managerOf.get(current); }
    trees.push(grow(current, true));
  }
  return trees.sort((a, b) => b.size - a.size || order.get(a.node.id) - order.get(b.node.id));
}

/** Everyone who reports up to this person, who can't become their manager. */
export function reportsBelow(edges, id) {
  const below = new Set();
  const pending = [id];
  while (pending.length) {
    const current = pending.pop();
    for (const edge of edges) {
      if (edge.source === current && !below.has(edge.target)) {
        below.add(edge.target);
        pending.push(edge.target);
      }
    }
  }
  return below;
}

/** True when following managers up from this person leads back to them. */
export function inReportingLoop(edges, id) {
  const managerOf = new Map(edges.map((edge) => [edge.target, edge.source]));
  const seen = new Set();
  for (let current = managerOf.get(id); current && !seen.has(current); current = managerOf.get(current)) {
    if (current === id) return true;
    seen.add(current);
  }
  return false;
}
